import { API_PATHS } from '@/constants/api-paths'
import { api } from '@/lib/api'
import type { ApiResponse, JobLocation, JobLocationPayload } from '@/types'

interface LocationListOptions {
  search?: string
  client_user?: string | number
  service_instance_id?: string | number
}

const toNumberOrNull = (value: number | string | null | undefined): number | null | undefined => {
  if (value === null || value === undefined || value === '') {
    return value as null | undefined
  }

  const parsed = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(parsed) ? parsed : undefined
}

const normalizeLocation = (location: JobLocation): JobLocation => ({
  ...location,
  latitude: toNumberOrNull(location.latitude) ?? null,
  longitude: toNumberOrNull(location.longitude) ?? null,
  coordinates: Array.isArray(location.coordinates)
    ? location.coordinates
        .filter((point): point is [number, number] => Array.isArray(point) && point.length === 2)
        .map((point): [number, number] => [Number(point[0]), Number(point[1])])
        .filter((point) => Number.isFinite(point[0]) && Number.isFinite(point[1]))
    : [],
  center_latlog: typeof location.center_latlog === 'string' ? location.center_latlog : null,
  active: location.active ?? true,
  deleted: location.deleted ?? false,
  linked_job_count: typeof location.linked_job_count === 'number' ? location.linked_job_count : Number(location.linked_job_count ?? 0),
  assigned_worker_count: typeof location.assigned_worker_count === 'number'
    ? location.assigned_worker_count
    : Number(location.assigned_worker_count ?? 0),
  online_worker_count: typeof location.online_worker_count === 'number'
    ? location.online_worker_count
    : Number(location.online_worker_count ?? 0),
})

export const locationsApi = {
  list: async (options: LocationListOptions = {}): Promise<JobLocation[]> => {
    const response = await api.get<JobLocation[]>(API_PATHS.locations.list, { params: options })
    return response.data.map(normalizeLocation)
  },

  get: async (id: string): Promise<JobLocation> => {
    const response = await api.get<ApiResponse<JobLocation> | JobLocation>(API_PATHS.locations.detail(id))
    const payload = 'data' in response.data ? response.data.data : response.data
    return normalizeLocation(payload)
  },

  create: async (payload: JobLocationPayload): Promise<JobLocation> => {
    const response = await api.post<ApiResponse<JobLocation>>(API_PATHS.locations.create, payload)
    return normalizeLocation(response.data.data)
  },

  update: async (id: string, payload: Partial<JobLocationPayload>): Promise<JobLocation> => {
    const response = await api.put<ApiResponse<JobLocation>>(API_PATHS.locations.update(id), payload)
    return normalizeLocation(response.data.data)
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(API_PATHS.locations.delete(id))
  },
}
